import { useState, useEffect, useRef } from 'react';
import logger from '../../utils/logging/logger';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const RESEND_COOLDOWN_SECONDS = 60;
// Shown for a wrong or expired code, new student or existing student, so the wording never changes on this screen.
export const INVALID_CODE_MESSAGE = 'The code is incorrect or has expired. Try again or request a new code.';

export default function useEnrollmentFlow() {
  const { courseIds, productIds } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, login, requestOtp, loginWithOtp } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userExists, setUserExists] = useState(null); // null = not checked, true = exists, false = doesn't exist
  const [checkingUser, setCheckingUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Existing students verify with a one-time emailed code by default (no
  // password to remember mid-checkout); 'password' is the fallback.
  const [authMethod, setAuthMethod] = useState('otp');
  const [otpCode, setOtpCode] = useState('');
  const [otpInfoMessage, setOtpInfoMessage] = useState('');
  // A code went out moments ago and none was re-sent: the notice counts down with the resend timer.
  const [cooldownNotice, setCooldownNotice] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimerRef = useRef(null);

  const paymentPath = courseIds
    ? `/enroll/multiple/${courseIds}`
    : `/purchase/products/${productIds}`;

  useEffect(() => {
    // If user is already authenticated, redirect to payment
    if (isAuthenticated && user?.email) {
      if (courseIds) {
        navigate(`/enroll/multiple/${courseIds}`);
      } else if (productIds) {
        navigate(`/purchase/products/${productIds}`);
      }
    }
  }, [isAuthenticated, user, courseIds, productIds, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    cooldownTimerRef.current = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(cooldownTimerRef.current);
  }, [resendCooldown]);

  const sendCode = async (emailToSend) => {
    // Enumeration-safe on the server: always reports success, so the UI shows
    // the same "code sent" feedback regardless of account state. The one extra it
    // reports here is a cooldown (a code went out moments ago, from any page).
    const result = await requestOtp(emailToSend, { reportCooldown: true });
    if (result && result.success === false) {
      setError(result.error || 'Failed to send code. Please try again.');
      return false;
    }
    if (result?.reason === 'cooldown') {
      setResendCooldown(result.retryAfterSeconds || RESEND_COOLDOWN_SECONDS);
      setOtpInfoMessage('');
      setCooldownNotice(true);
      return 'cooldown';
    }
    setCooldownNotice(false);
    setOtpCode('');
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    return true;
  };

  // A new email must be confirmed before payment: a code is mailed to it, and only the right code
  // opens the payment form. That removes typos in the address at the source.
  const sendNewEmailCode = async (emailToSend) => {
    let response;
    try {
      response = await api.guestRequestEmailCode(emailToSend);
    } catch (requestError) {
      logger.error('Failed to send email verification code', { error: requestError });
      setError(requestError.message || 'Failed to send the code. Please try again.');
      return false;
    }
    if (response?.reason === 'cooldown') {
      // A code went out moments ago (page refreshed, or the email re-entered): no new one was sent.
      const seconds = response.retryAfterSeconds || RESEND_COOLDOWN_SECONDS;
      setResendCooldown(seconds);
      setOtpInfoMessage('');
      setCooldownNotice(true);
      return 'cooldown';
    }
    setCooldownNotice(false);
    setOtpCode('');
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    return true;
  };

  const checkUserExists = async (emailToCheck) => {
    if (!emailToCheck || !emailToCheck.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setCheckingUser(true);
    setError('');

    try {
      const userExistsResponse = await api.checkUserExists(emailToCheck);
      setUserExists(userExistsResponse.exists);

      if (!userExistsResponse.exists) {
        // New email: confirm it with an emailed code first (verified in handleFormSubmit). If no code
        // could be sent (e.g. the hourly limit), stay on the email step so the screen does not claim one was.
        const sent = await sendNewEmailCode(emailToCheck);
        if (!sent) setUserExists(null);
        return;
      }

      // Existing student: send the code right away so they can verify inline.
      setAuthMethod('otp');
      await sendCode(emailToCheck);
    } catch (error) {
      logger.error('Error checking user existence', { error });
      setError('Failed to check account status. Please try again.');
    } finally {
      setCheckingUser(false);
    }
  };

  const completeSignIn = (result, fallbackMessage) => {
    if (result?.success) {
      navigate(paymentPath);
    } else {
      setError(result?.error || fallbackMessage);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (userExists === null) {
      // First step: check if user exists
      await checkUserExists(email);
      return;
    }

    if (userExists === false) {
      // New student confirming their email: the right code opens the payment form.
      if (!otpCode || otpCode.length !== 6) {
        setError('Please enter the 6-digit code.');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const result = await api.guestVerifyEmailCode(email, otpCode);
        const guestPath = courseIds ? `/enroll/guest/${courseIds}` : `/enroll/guest/products/${productIds}`;
        navigate(guestPath, { state: { email, emailToken: result.emailToken } });
      } catch (verifyError) {
        // a 400 is the server saying the code is wrong/expired; anything else (network...) keeps its own message
        setError(verifyError.status === 400 ? INVALID_CODE_MESSAGE : (verifyError.message || INVALID_CODE_MESSAGE));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (userExists !== true) return;

    setError('');

    if (authMethod === 'otp') {
      if (!otpCode || otpCode.length !== 6) {
        setError('Please enter the 6-digit code.');
        return;
      }

      setLoading(true);
      try {
        const result = await loginWithOtp(email, otpCode);
        if (!result?.success && /invalid or expired/i.test(result?.error || '')) {
          setError(INVALID_CODE_MESSAGE);
        } else {
          completeSignIn(result, INVALID_CODE_MESSAGE);
        }
      } catch (error) {
        logger.error('OTP login error', { error });
        setError('Verification failed. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Password fallback
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      completeSignIn(result, 'Login failed. Please try again.');
    } catch (error) {
      logger.error('Login error', { error });
      setError('Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);
    const sent = userExists === false ? await sendNewEmailCode(email) : await sendCode(email);
    if (sent === true) setOtpInfoMessage('A new code has been sent.');
    setLoading(false);
  };

  const handleToggleAuthMethod = async (method) => {
    setError('');
    setAuthMethod(method);
    if (method === 'otp' && resendCooldown === 0 && !otpCode) {
      await sendCode(email);
    }
  };

  // "Existing User? Sign In": the regular sign-in page, returning here afterwards
  // (an already signed-in user is then forwarded straight to payment).
  const goToSignIn = () => {
    const returnTo = courseIds ? `/enroll/flow/${courseIds}` : `/enroll/products/${productIds}`;
    navigate(`/login?redirect=${encodeURIComponent(returnTo)}`);
  };

  // Back to the email step with the typed address still in the box. The email is locked while a
  // code is out or a sign-in is in progress; this is the one way to edit it.
  const handleChangeEmail = () => {
    setError('');
    setUserExists(null);
    setPassword('');
    setShowPassword(false);
    setOtpCode('');
    setOtpInfoMessage('');
    setCooldownNotice(false);
    setResendCooldown(0);
    setAuthMethod('otp');
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  return {
    courseIds,
    email,
    password,
    setPassword,
    loading,
    error,
    userExists,
    checkingUser,
    showPassword,
    setShowPassword,
    authMethod,
    otpCode,
    setOtpCode,
    otpInfoMessage: cooldownNotice
      ? (resendCooldown > 0
        ? `We recently sent you a code. Check your inbox or spam folder, or request another in ${resendCooldown} ${resendCooldown === 1 ? 'second' : 'seconds'}.`
        : '')
      : otpInfoMessage,
    resendCooldown,
    handleResendCode,
    handleToggleAuthMethod,
    goToSignIn,
    handleFormSubmit,
    handleEmailChange,
    handleChangeEmail
  };
}
